import { Button, cn, Row, ThemeColor, withColor } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCheckCircle2,
  LucideFile,
  LucideLoader2,
  LucideX,
} from 'lucide-react';

import { $pendingAttachments } from '../../_state/chatInput.state';
import type { PendingAttachment } from '../../types';

interface AttachmentPreviewProps {
  onRemove: (id: string) => void;
}

type AttachmentByStatusProps = {
  attachment: PendingAttachment;
};

const colorByAttachmentStatus: Record<string, ThemeColor> = {
  error: 'failure',
  uploading: 'neutral',
  confirmed: 'success',
};

//
// ⚛️ ⚛️ ⚛️ ATTACHMENT PREVIEW ⚛️ ⚛️ ⚛️
//
export const AttachmentPreview = ({ onRemove }: AttachmentPreviewProps) => {
  // Atoms
  const attachments = useAtomValue($pendingAttachments);

  if (attachments.length === 0) return null;

  return (
    <Row gap2 className="@AttachmentPreview flex-wrap px-3">
      {attachments.map((attachment) => {
        const color = colorByAttachmentStatus[attachment.status];

        return (
          <div
            key={attachment.id}
            style={withColor(color)}
            className={cn(
              'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
              'border-custom-color/30 bg-custom-color/10 text-custom-color',
              color === 'neutral' &&
                'border-neutral/20 bg-neutral/5 text-neutral/70',
            )}
          >
            <AttachmentByStatus attachment={attachment} />

            <span className="max-w-[120px] truncate">
              {attachment.file.name}
            </span>

            {attachment.status === 'uploading' && (
              <span className="text-neutral/50 tabular-nums">
                {attachment.progress}%
              </span>
            )}

            <Button
              variant="text"
              color={color}
              size="small"
              onClick={() => onRemove(attachment.id)}
              className="p-1"
            >
              <LucideX size={10} />
            </Button>
          </div>
        );
      })}
    </Row>
  );
};

//
// ⚛️ ⚛️ ⚛️ CHAT MESSAGE EVENT CONTENT ⚛️ ⚛️ ⚛️
//
const AttachmentByStatus = ({ attachment }: AttachmentByStatusProps) => {
  switch (attachment.status) {
    case 'uploading':
      return <LucideLoader2 size={14} className="animate-spin" />;
    case 'confirmed':
      return <LucideCheckCircle2 size={14} />;
    case 'error':
      return <LucideFile size={14} />;
  }
};
